import React from "react";
import {
  House, Users, Buildings, Broadcast, ListChecks, Warning, ClockCounterClockwise,
  UploadSimple, PlugsConnected, SignOut, ShieldCheck, ChartBar,
  Bell, PaperPlaneTilt, ArrowsSplit, PaperPlaneRight, FileText, Envelope, LockKey, Pulse, Ticket, BookOpen,
  DownloadSimple, Gear, Code, Storefront, Percent, Target, MapTrifold, ChartLineUp, XCircle, PauseCircle, Crosshair,
  Phone, TestTube, ListDashes, Tray, ShareNetwork, ClipboardText, Lifebuoy,
} from "@phosphor-icons/react";
import { ROLES } from "@/lib/constants";
import NavItem from "./NavItem";
import { MenuConfigContext, useMenuConfig } from "./menuConfig";
import { useInboxCount } from "./useInboxCount";
import { useRescueCount } from "./useRescueCount";
import { useNoBuyerCount } from "./useNoBuyerCount";
import { useDeliveryAlertCount } from "./useDeliveryAlertCount";
import { NewUserWizardButton } from "@/components/user-wizard/NewUserWizardButton";

/**
 * Left sidebar: brand, primary nav, sign-in strip.
 * Kept as a dumb, permission-aware presenter so Layout stays a pure shell.
 */
export default function Sidebar(props) {
  const menu = useMenuConfig(props.user);
  return (
    <MenuConfigContext.Provider value={menu}>
      <SidebarBody {...props} wizardOrder={(menu.items["/employees"]?.order ?? menu.order["/employees"] ?? 0) + 5} />
    </MenuConfigContext.Provider>
  );
}

function SidebarBody({ user, platform, hasPerm, onSignOut, wizardOrder }) {
  const isInternal = user?.org_type === "internal";
  const isMediaBuyer = user?.role === "media_buyer";
  const canInbox = isInternal && ["super_admin", "admin", "employee"].includes(user?.role);
  const inboxCount = useInboxCount(canInbox);
  const rescueCount = useRescueCount(canInbox);
  const noBuyerCount = useNoBuyerCount(isInternal && ["super_admin", "admin"].includes(user?.role));
  const deliveryAlertCount = useDeliveryAlertCount(isInternal && !isMediaBuyer);

  // Media buyers get their own minimal nav — no PII, no financials.
  if (isMediaBuyer) {
    return (
      <aside className="border-r border-black bg-white flex flex-col">
        <div className="px-5 py-5 border-b border-black">
          <div
            className="font-chivo font-black text-2xl tracking-tighter leading-none uppercase"
            data-testid="platform-name"
          >
            {platform.short_name || platform.name}
          </div>
          <div className="label-mono mt-1">Media buyer console</div>
        </div>
        <nav className="flex-1 py-2 flex flex-col" data-testid="sidebar-nav">
          <NavItem to="/admin/landing-pages" icon={Storefront} label="Landing Pages" testId="nav-landing-pages" />
          <NavItem to="/admin/pixels" icon={Target} label="Marketing Pixels" testId="nav-pixels" />
          <NavItem to="/media-buyer/custom-tracking" icon={Code} label="Custom Tracking" testId="nav-mb-custom-tracking" />
          <NavItem to="/postbacks" icon={Crosshair} label="Postbacks" testId="nav-postbacks" />
          <NavItem to="/media-buyer/attribution" icon={ChartBar} label="Attribution" testId="nav-mb-attribution" />
          <NavItem to="/results" icon={ChartLineUp} label="Traffic Results" testId="nav-mb-results" />
          <NavItem to="/timeline" icon={ClockCounterClockwise} label="Activity" testId="nav-activity" />
          <NavItem to="/help" icon={BookOpen} label="Help" testId="nav-help" />
        </nav>
        <SidebarFooter user={user} onSignOut={onSignOut} />
      </aside>
    );
  }

  return (
    <aside className="border-r border-black bg-white flex flex-col">
      <div className="px-5 py-5 border-b border-black">
        <div
          className="font-chivo font-black text-2xl tracking-tighter leading-none uppercase"
          data-testid="platform-name"
        >
          {platform.short_name || platform.name}
        </div>
        <div className="label-mono mt-1">{platform.tagline}</div>
      </div>
      <nav className="flex-1 py-2 flex flex-col" data-testid="sidebar-nav">
        <NavItem to="/" icon={House} label="Dashboard" testId="nav-dashboard" />
        <NavItem to="/leads" icon={ListChecks} label="Leads" testId="nav-leads" />
        <NavItem to="/bad-leads" icon={Warning} label="Bad Leads / Returns" testId="nav-badleads" />
        {user?.org_type !== "affiliate" && hasPerm("export_leads") && (
          <NavItem to="/downloads" icon={DownloadSimple} label="Downloads" testId="nav-downloads" />
        )}
        {user?.org_type !== "affiliate" && (
          <NavItem to="/replacement-vouchers" icon={Ticket} label="Replacement Vouchers" testId="nav-vouchers" />
        )}
        {user?.org_type === "affiliate" && (
          <NavItem to="/postbacks" icon={Crosshair} label="Postbacks" testId="nav-postbacks" />
        )}
        {isInternal && (
          <>
            <NavItem to="/clients" icon={Buildings} label="Clients" testId="nav-clients" />
            <NavItem to="/affiliates" icon={Broadcast} label="Affiliates" testId="nav-affiliates" />
            <NavItem to="/admin/landing-pages" icon={Storefront} label="Landing Pages" testId="nav-landing-pages" />
            {canInbox && (
              <NavItem to="/admin/requests" icon={Tray} label="Request Inbox" testId="nav-request-inbox" badge={inboxCount} />
            )}
            {canInbox && (
              <NavItem to="/admin/lead-rescue" icon={Lifebuoy} label="Lead Rescue" testId="nav-lead-rescue" badge={rescueCount} />
            )}
            <NavItem to="/results" icon={ChartLineUp} label="Traffic Results" testId="nav-traffic-results" />
            {["super_admin", "admin"].includes(user?.role) && (
              <>
                <NavItem to="/admin/onboarding" icon={ClipboardText} label="Buyer Onboarding" testId="nav-buyer-onboarding" />
                <NavItem to="/admin/demand-map" icon={MapTrifold} label="Demand Map" testId="nav-demand-map" />
                <NavItem to="/admin/traffic-access" icon={ShareNetwork} label="Traffic Access" testId="nav-traffic-access" badge={noBuyerCount} />
                <NavItem to="/admin/test-leads" icon={TestTube} label="Test Leads" testId="nav-test-leads" />
                <NavItem to="/admin/call-taps" icon={Phone} label="Call Taps" testId="nav-call-taps" />
                <NavItem to="/admin/google-analytics" icon={ChartBar} label="Google Analytics" testId="nav-google-analytics" />
              </>
            )}
            {hasPerm("landing_pages.manage_pixels") && (
              <NavItem to="/admin/pixels" icon={Target} label="Marketing Pixels" testId="nav-pixels" />
            )}
            {hasPerm("landing_pages.request_custom_tracking") && (
              <NavItem to="/media-buyer/custom-tracking" icon={Code} label="Custom Tracking" testId="nav-mb-custom-tracking" />
            )}
            {hasPerm("landing_pages.approve_custom_tracking") && (
              <NavItem to="/admin/tracking-approvals" icon={ShieldCheck} label="Tracking Approvals" testId="nav-tracking-approvals" />
            )}
            {hasPerm("landing_pages.approve_custom_tracking") && (
              <NavItem to="/admin/postbacks" icon={Crosshair} label="Postback Approvals" testId="nav-postback-approvals" />
            )}
            <NavItem to="/admin/marketplace" icon={ChartBar} label="Marketplace" testId="nav-marketplace" />
            <NavItem to="/admin/margin" icon={Percent} label="Margin (internal)" testId="nav-margin" />
            <NavItem to="/campaigns" icon={ChartBar} label="Campaigns" testId="nav-campaigns" />
          </>
        )}
        <NavItem to="/employees" icon={Users} label="Employees" testId="nav-employees" />
        <div style={{ order: wizardOrder }}><NewUserWizardButton variant="nav" testId="nav-new-user-wizard" /></div>
        {isInternal && (
          <>
            <NavItem to="/csv-upload" icon={UploadSimple} label="CSV Upload" testId="nav-csv" />
            <NavItem to="/email-intake" icon={Envelope} label="Email Intake" testId="nav-email-intake" />
            <NavItem to="/delivery" icon={PaperPlaneRight} label="Delivery Destinations" testId="nav-delivery" />
            <NavItem to="/delivery-health" icon={Pulse} label="Delivery Health" testId="nav-delivery-health" badge={deliveryAlertCount} />
            <NavItem to="/admin/failed-deliveries" icon={Warning} label="Failed Deliveries" testId="nav-failed-deliveries" />
            {hasPerm("landing_pages.share_previews") && (
              <NavItem to="/admin/prospect-engagement" icon={Broadcast} label="Prospect Engagement" testId="nav-prospect-engagement" />
            )}
            <NavItem to="/invoices" icon={FileText} label="Invoices" testId="nav-invoices" />
            <NavItem to="/integrations" icon={PlugsConnected} label="Integrations" testId="nav-integrations" />
            <NavItem to="/form-integrations" icon={ArrowsSplit} label="Form Adapters" testId="nav-form-integrations" />
            {hasPerm("manage_integrations") && (
              <NavItem to="/quality-alerts" icon={Bell} label="Quality Alerts" testId="nav-quality" />
            )}
            <NavItem to="/scheduled-reports" icon={PaperPlaneTilt} label="Scheduled Reports" testId="nav-reports" />
          </>
        )}
        {hasPerm("view_audit_logs") && (
          <NavItem to="/audit" icon={ShieldCheck} label="Audit Log" testId="nav-audit" />
        )}
        {user?.role === "super_admin" && (
          <NavItem to="/mfa-policies" icon={LockKey} label="MFA Policies" testId="nav-mfa-policies" />
        )}
        {user?.role === "super_admin" && (
          <NavItem to="/settings/platform" icon={Gear} label="Platform Settings" testId="nav-platform-settings" />
        )}
        {user?.role === "super_admin" && (
          <NavItem to="/admin/menu" icon={ListDashes} label="Menu Settings" testId="nav-menu-settings" />
        )}
        {user?.role === "super_admin" && (
          <NavItem to="/admin/code-health" icon={Code} label="Code Health" testId="nav-code-health" />
        )}
        {user?.role === "super_admin" && (
          <NavItem to="/admin/affiliate-quality" icon={ChartBar} label="Affiliate Quality" testId="nav-affiliate-quality" />
        )}
        {isInternal && (user?.role === "super_admin" || hasPerm("view_dashboard")) && (
          <NavItem to="/admin/buyer-rejections" icon={XCircle} label="Buyer Rejections" testId="nav-buyer-rejections" />
        )}
        {isInternal && (user?.role === "super_admin" || hasPerm("view_dashboard")) && (
          <NavItem to="/admin/paused-traffic" icon={PauseCircle} label="Paused Traffic" testId="nav-paused-traffic" />
        )}
        <NavItem to="/timeline" icon={ClockCounterClockwise} label="Activity" testId="nav-activity" />
        <NavItem to="/help" icon={BookOpen} label="Guide" testId="nav-help" />
      </nav>
      <SidebarFooter user={user} onSignOut={onSignOut} />
    </aside>
  );
}

function SidebarFooter({ user, onSignOut }) {
  return (
    <div className="border-t border-black p-4">
      <div className="label-mono">Signed in as</div>
      <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
      <div className="text-[11px] text-zinc-500 truncate" data-testid="current-user-email">{user?.email}</div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-[10px] uppercase tracking-widest bg-black text-white px-2 py-0.5">
          {ROLES[user?.role] || user?.role}
        </span>
        <button
          onClick={onSignOut}
          data-testid="logout-button"
          className="text-[11px] uppercase tracking-widest border border-black px-2 py-1 hover:bg-black hover:text-white transition-colors duration-100"
        >
          <SignOut size={12} className="inline mr-1" /> Sign out
        </button>
      </div>
    </div>
  );
}
